import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { BiData, EditWithMuroBiEvent } from '@wix/da-bi/pkg/events';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import DaDrawLogoAndTextColoredAndBranded from '@wix/da-ds/pkg/DaDrawLogoAndTextColoredAndBranded';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import PlaygroundEmptyStateDesktop from '../../PlaygroundEmptyStateDesktop';
import PlaygroundEmptyStateMobile from '../../PlaygroundEmptyStateMobile';
import { usePushMiniMobileMuroModal } from '@wix/da-shared-react/pkg/utils/hooks/usePushMuroModal';
import { useReducedMotion } from '@wix/da-hooks/pkg/useReducedMotion';

import s from './DrawEmptyState.scss';

interface Props {}

const DEMO_URL = 'muro/draw_demo.mp4';

const DrawEmptyState: FC<Props> = () => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const pushMiniMobileMuroModal = usePushMiniMobileMuroModal();
  const isReducedMotionEnabled = useReducedMotion();

  return isMobile ? (
    <PlaygroundEmptyStateMobile
      heading={t('playground.emptyState.draw.heading.mobile.hasMobileDraw', {
        postProcess: ['reactPostprocessor'],
        alt: (
          <VisuallyHidden>
            {t('playground.emptyState.draw.heading.mobile.alt')}
          </VisuallyHidden>
        ),
        logo: <DaDrawLogoAndTextColoredAndBranded className={s['logo']} />,
      })}
      description={t(
        'playground.emptyState.draw.description.mobile.hasMobileDraw'
      )}
      callToAction={
        <BrandedButton
          size="medium"
          variant="dreamup-purple"
          biData={BiData<EditWithMuroBiEvent>({
            evid: 926,
            sectionname: 'playground_empty_state',
            // No typeid or itemid for empty state pages
          })}
          onClick={() => {
            pushMiniMobileMuroModal();
          }}
        >
          {t('playground.emptyState.draw.ctaLabel')}
        </BrandedButton>
      }
    />
  ) : (
    <PlaygroundEmptyStateDesktop
      logo={<DaDrawLogoAndTextColoredAndBranded />}
      heading={t('playground.emptyState.draw.heading.desktop')}
      description={t('playground.emptyState.draw.description.desktop')}
      callToAction={
        <BrandedButton
          size="medium"
          variant="dreamup-purple"
          biData={BiData<EditWithMuroBiEvent>({
            evid: 926,
            sectionname: 'playground_empty_state',
            // No typeid or itemid for empty state pages
          })}
          href={Url.studio({
            playground: 'draw',
            openSubmitForm: true,
          })}
        >
          {t('playground.emptyState.draw.ctaLabel')}
        </BrandedButton>
      }
      postfix={
        <video
          className={s['demoVideo']}
          autoPlay={!isReducedMotionEnabled}
          controls
          muted
          loop
          playsInline // required to disable fullscreen iOS
          aria-label={t('playground.emptyState.draw.videoLabel')}
          height={329}
          src={Url.staticCDNLink(DEMO_URL)}
        ></video>
      }
    />
  );
};

DrawEmptyState.displayName = 'DrawEmptyState';

export default DrawEmptyState;
